import type {CopilotChatModel} from './copilot-chat-types'

const TOKENS_PER_MILLION = 1_000_000
const NANO_AIU_DIVISOR = 1_000_000_000

type TokenPrices = NonNullable<NonNullable<CopilotChatModel['billing']>['token_prices']>

export type NormalizedTierPrices = {
  inputPrice: number
  outputPrice: number
  cachePrice: number
}

export type NormalizedPricing = {
  defaultTier: NormalizedTierPrices
  longContextTier?: NormalizedTierPrices
}

/**
 * Converts raw CAPI token prices into AICs per million tokens.
 * Handles both the legacy flat format (pre-2026-06-01, prices in nano-AIUs)
 * and the tiered format (2026-06-01+, prices in AIUs with default/long_context tiers).
 */
export function normalizeTokenPrices(tokenPrices: TokenPrices | undefined): NormalizedPricing | undefined {
  if (!tokenPrices || tokenPrices.batch_size <= 0) return undefined
  const scale = TOKENS_PER_MILLION / tokenPrices.batch_size

  // 2026-06-01+ tiered format: prices already in AIUs, nested under `default`/`long_context`.
  // 2026-07-01+ renames `cache_price` → `cache_read_price` and `context_max` → `max_prompt_tokens`.
  // Prefer new names; fall back to old names for 2026-06-01 responses.
  if (tokenPrices.default) {
    const def = tokenPrices.default
    const result: NormalizedPricing = {
      defaultTier: {
        inputPrice: def.input_price * scale,
        outputPrice: def.output_price * scale,
        cachePrice: (def.cache_read_price ?? def.cache_price ?? 0) * scale,
      },
    }

    if (tokenPrices.long_context) {
      const lc = tokenPrices.long_context
      result.longContextTier = {
        inputPrice: lc.input_price * scale,
        outputPrice: lc.output_price * scale,
        cachePrice: (lc.cache_read_price ?? lc.cache_price ?? 0) * scale,
      }
    }
    return result
  }

  // Legacy flat format (pre-2026-06-01): prices in nano-AIUs at the top level
  if (tokenPrices.input_price !== undefined) {
    return {
      defaultTier: {
        inputPrice: (tokenPrices.input_price / NANO_AIU_DIVISOR) * scale,
        outputPrice: ((tokenPrices.output_price ?? 0) / NANO_AIU_DIVISOR) * scale,
        cachePrice: ((tokenPrices.cache_price ?? 0) / NANO_AIU_DIVISOR) * scale,
      },
    }
  }

  return undefined
}

/**
 * Calculates the actual AI credits consumed for a given number of tokens
 * using the model's normalized per-million-token pricing.
 */
export function calculateCredits(tokens: number, pricePerMillion: number): number {
  return (tokens / TOKENS_PER_MILLION) * pricePerMillion
}

/**
 * Returns the total AI credits for a single assistant turn, rounded to two
 * decimal places. Uses the model's default tier pricing for both input and
 * output tokens.
 */
export function calculateTurnCredits(
  usage: {inputTokens: number; outputTokens: number},
  pricing: NormalizedPricing,
): number {
  return (
    Math.round(
      (calculateCredits(usage.inputTokens, pricing.defaultTier.inputPrice) +
        calculateCredits(usage.outputTokens, pricing.defaultTier.outputPrice)) *
        100,
    ) / 100
  )
}
